import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, merge, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    startWith,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { CopilotSidePanelService } from './copilot-side-panel.service';
import { CopilotChatController } from './copilot-chat-controller';
import { PAGE_CONTEXT_FORMAT_NOTE, PageContextService } from './page-context.service';

/** Request body for the backchatty Ask endpoint (chatservice AskRequest). */
export interface AskRequest {
    query: string;
    /** JSON Schema string; blocking Ask only. Constrains the response to structured JSON. */
    response_schema?: string;
    /** Optional client-supplied trace id; the server generates one if absent. */
    req_msg_id?: string;
}

/** Response body for the blocking Ask endpoint (chatservice AskResponse). */
export interface AskResponse {
    /** Free text, or — when response_schema is sent — a JSON string conforming to it. */
    response: string;
}

export type SuggestionsStateName = 'idle' | 'loading' | 'ready' | 'error';

export interface SuggestionsState {
    state: SuggestionsStateName;
    questions: string[];
}

/**
 * Route-data key marking a page as covered by a Copilot agent with data tools, and therefore
 * eligible for suggested questions. Opt-in: a page that does not set it gets no questions.
 */
const ROUTE_SUGGESTIONS_KEY = 'copilotPageSuggestions';

const TRIGGER_DEBOUNCE_MS = 150;
/** Blocking backchatty Ask endpoint (via the staging Kong `backchatty-ask` route). */
const ENDPOINT = '/api/1.0/chat/ask';
/** Upper bound on how many suggested questions we ask for / render. */
const MAX_QUESTIONS = 5;
/** Ceiling for a single Ask request. A backend that accepts the connection but never responds
 * would otherwise leave the UI stuck on the loading skeletons indefinitely. */
const REQUEST_TIMEOUT_MS = 30_000;
/** Backend cap on the AskRequest `query` field (chars), per chat_service.proto / ask.py MAX_QUERY_LENGTH. */
const MAX_QUERY_LENGTH = 100_000;
/** Keep the query safely under the backend cap (95%); the page content is trimmed to fit if needed. */
const QUERY_CHAR_BUDGET = Math.floor(MAX_QUERY_LENGTH * 0.95);
/** Marker appended when page content is trimmed to fit the query budget. */
const TRUNCATION_NOTE = '\n…[page content truncated to fit length limit]';

/**
 * JSON Schema constraining the Ask response to a bounded list of question strings.
 * `title` is REQUIRED: the Ask backend feeds this schema to LangChain's
 * `with_structured_output()`, which uses the title as the tool/function name.
 * Without a title the call fails with gRPC INTERNAL (verified against staging).
 */
const QUESTIONS_SCHEMA = JSON.stringify({
    title: 'SuggestedQuestions',
    description: "A short list of suggested follow-up questions about the user's Pure1 fleet.",
    type: 'object',
    properties: {
        questions: {
            type: 'array',
            items: { type: 'string' },
            maxItems: MAX_QUESTIONS,
        },
    },
    required: ['questions'],
});

/**
 * Builds the Ask `query`: the page context (URL + stripped text) plus the explicit instruction.
 * The task lives in the query — not `system_instruction`, which is dev-only and may be ignored in
 * prod — while the server's own system prompt handles scoping to Pure1/Everpure topics.
 *
 * The page content is trimmed if needed so the whole query stays within QUERY_CHAR_BUDGET
 * (95% of the backend's MAX_QUERY_LENGTH); the leading framing and trailing instruction are
 * always preserved so the model still knows what to do.
 */
function buildPageQuestionsQuery(url: string, html: string): string {
    const intro =
        'You are assisting a user of the Pure1 fleet-management console.\n' +
        `They are currently viewing this page (URL: ${url}). Its visible content follows. ` +
        `${PAGE_CONTEXT_FORMAT_NOTE}\n\n`;
    const outro =
        `\n\nBased on the specific data shown above, suggest up to ${MAX_QUESTIONS} follow-up questions this ` +
        'user is most likely to ask next about exactly what they are viewing. Make every question:\n' +
        "- Specific to this page's actual content: reference the concrete appliances, models, metrics, " +
        'statuses, or values shown above by their real names/values — never generic phrasing that would ' +
        'fit any page.\n' +
        '- A drill-down into what is on screen (compare, explain, or investigate specific items shown), ' +
        'not a broad how-to or setup question.\n' +
        '- Distinct from the others, concise, and answerable from Pure1 data.\n' +
        'Return them using the provided response schema.';

    const htmlBudget = Math.max(0, QUERY_CHAR_BUDGET - intro.length - outro.length - TRUNCATION_NOTE.length);
    const body = html.length > htmlBudget ? html.slice(0, htmlBudget) + TRUNCATION_NOTE : html;
    return intro + body + outro;
}

/** Parses the Ask response body into a clean list of question strings. Tolerates either the
 * schema-shaped `{ questions: [...] }` object or a bare string array. Returns [] on anything else. */
function parseQuestions(raw: string | undefined): string[] {
    if (!raw) {
        return [];
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (_) {
        return [];
    }
    const list = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object' && Array.isArray((parsed as { questions?: unknown }).questions)
          ? (parsed as { questions: unknown[] }).questions
          : [];
    return list
        .filter((q): q is string => typeof q === 'string')
        .map(q => q.trim())
        .filter(q => q.length > 0)
        .slice(0, MAX_QUESTIONS);
}

@Injectable({ providedIn: 'root' })
export class CopilotPageSuggestionsService implements OnDestroy {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly sidePanel = inject(CopilotSidePanelService);
    private readonly controller = inject(CopilotChatController);
    private readonly pageContext = inject(PageContextService);

    private readonly stateSubject = new BehaviorSubject<SuggestionsState>({
        state: 'idle',
        questions: [],
    });
    readonly state$: Observable<SuggestionsState> = this.stateSubject.asObservable();

    /** Fired by `refresh()` to bypass the URL-distinct check and force a new fetch. */
    private readonly refreshSubject = new Subject<void>();

    private readonly destroy$ = new Subject<void>();
    private subscription: Subscription | null = null;
    private currentAbort: AbortController | null = null;
    /** Per-request timeout ceiling (overridable in tests). */
    private readonly requestTimeoutMs = REQUEST_TIMEOUT_MS;

    /** Wires up the trigger pipeline. Call once during app bootstrap. */
    start(): void {
        if (this.subscription) {
            return;
        }
        const url$ = this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(e => e.urlAfterRedirects.split('?')[0]),
            startWith(this.router.url.split('?')[0]),
            distinctUntilChanged(),
        );

        // Suggestions are shown only while the panel is open on an empty conversation.
        const shouldShow$ = combineLatest([this.sidePanel.isOpen$, this.controller.conversation$]).pipe(
            map(([open, convo]) => open && (convo?.messages?.length ?? 0) === 0),
            distinctUntilChanged(),
        );

        // Regenerate every time the panel is shown again on an empty conversation — including
        // reopening on the SAME page — so a fresh set of questions is fetched on each open.
        const shown$ = shouldShow$.pipe(
            filter(shouldShow => shouldShow),
            map(() => undefined),
        );

        // Also regenerate when the route changes while suggestions are on screen, so the pills
        // track the new page. A same-URL navigation is dropped by distinctUntilChanged above.
        const urlChangedWhileShown$ = url$.pipe(
            withLatestFrom(shouldShow$),
            filter(([, shouldShow]) => shouldShow),
            map(() => undefined),
        );

        const trigger$ = merge(shown$, urlChangedWhileShown$);

        // Clear any stale pills and show the loading skeleton the instant the panel is shown
        // (or the page changes under it), before the debounce + fetch window, so the previous
        // questions never flash while the new ones are being generated.
        //
        // A page that has not opted in gets no questions at all, so it goes straight to idle
        // instead — never flashing a skeleton for pills that will not arrive — and anything
        // still in flight from the previous page is aborted here rather than landing on a page
        // that is not supposed to show questions.
        this.subscription = trigger$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (!this.suggestionsEnabledForCurrentPage()) {
                this.currentAbort?.abort();
                this.currentAbort = null;
                this.stateSubject.next({ state: 'idle', questions: [] });
                return;
            }
            this.stateSubject.next({ state: 'loading', questions: [] });
        });

        // Debounced (re)generation. Re-check shouldShow at fire time: opening with a prompt
        // (or a fast close) can make the conversation non-empty / close the panel during the
        // debounce, and we must not fetch then. refresh() bypasses this to force a re-roll.
        const debouncedFetch$ = trigger$.pipe(
            debounceTime(TRIGGER_DEBOUNCE_MS),
            withLatestFrom(shouldShow$),
            filter(([, shouldShow]) => shouldShow),
        );

        this.subscription.add(
            merge(debouncedFetch$, this.refreshSubject)
                .pipe(
                    takeUntil(this.destroy$),
                    // Routed through switchMap rather than filtered out, so that arriving on an
                    // uncovered page still cancels whatever the previous page left in flight
                    // (a filter would leave the old request to resolve and overwrite idle).
                    switchMap(() =>
                        this.suggestionsEnabledForCurrentPage()
                            ? this.fetchSuggestions()
                            : of<SuggestionsState>({ state: 'idle', questions: [] }),
                    ),
                )
                .subscribe(state => this.stateSubject.next(state)),
        );

        // Abort any in-flight fetch and reset to idle when the user starts a chat.
        // A separate subscription (not tap+filter on the trigger) is required so that
        // the AbortController fires even when switchMap has no new trigger to switch on —
        // otherwise the in-flight fetch would complete and overwrite the idle state.
        // Closing the panel does NOT reset here — reopening re-triggers a fresh fetch above.
        this.subscription.add(
            this.controller.conversation$
                .pipe(
                    filter(convo => (convo?.messages?.length ?? 0) > 0),
                    takeUntil(this.destroy$),
                )
                .subscribe(() => {
                    this.currentAbort?.abort();
                    this.currentAbort = null;
                    this.stateSubject.next({ state: 'idle', questions: [] });
                }),
        );
    }

    stop(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.currentAbort?.abort();
        this.currentAbort = null;
    }

    /** Re-roll the suggestions for the current page without changing routes. */
    refresh(): void {
        this.refreshSubject.next();
    }

    /**
     * Whether the current page opted into suggested questions via `copilotPageSuggestions` route
     * data. The deepest declaration on the activated path wins, so a child route can opt out of a
     * parent's opt-in; absent (or `false`) means no questions.
     *
     * Questions are opt-in because they are only useful where Copilot has an agent with data
     * tools behind the page — elsewhere they invited questions the backend could not answer.
     * Gates the side panel only: `fetchSuggestionsOnce()` serves standalone hosts such as the
     * Subscription Assessment "Ask CoPilot" card, which decide for themselves.
     */
    private suggestionsEnabledForCurrentPage(): boolean {
        let node: ActivatedRouteSnapshot | null = this.router.routerState?.snapshot?.root ?? null;
        let enabled: boolean | undefined;
        while (node) {
            const value = node.data?.[ROUTE_SUGGESTIONS_KEY];
            if (typeof value === 'boolean') {
                enabled = value;
            }
            node = node.firstChild;
        }
        return enabled === true;
    }

    /**
     * One-off suggestions fetch for standalone consumers (e.g. the Subscription
     * Assessment "Ask CoPilot" card) that render outside the side panel. Runs the
     * same page-context fetch as the panel trigger, but independently of the
     * shared trigger pipeline and `state$`, and without touching the shared abort
     * — so it can neither cancel nor be cancelled by the panel's in-flight request.
     */
    fetchSuggestionsOnce(): Observable<SuggestionsState> {
        return this.fetchSuggestions(false);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stop();
    }

    /**
     * Fetches suggestions via a single blocking call to the backchatty Ask endpoint.
     * Sends the page context as the `query` plus a `response_schema` constraining the
     * answer to a JSON list of questions, then parses that list. Emits `loading` while
     * in flight, then `ready` (with questions) or `error`.
     *
     * `trackShared` (panel trigger) also stores the request's AbortController on the
     * shared `currentAbort` so chat-start / navigation can cancel it. Standalone
     * one-off fetches pass `false` to stay isolated from that shared lifecycle.
     */
    private fetchSuggestions(trackShared = true): Observable<SuggestionsState> {
        const { url, html } = this.pageContext.captureCurrent();
        const query = buildPageQuestionsQuery(url, html);

        return new Observable<SuggestionsState>(observer => {
            observer.next({ state: 'loading', questions: [] });

            const abort = new AbortController();
            if (trackShared) {
                // Cancel any previous in-flight shared request and track this one.
                this.currentAbort?.abort();
                this.currentAbort = abort;
            }

            // Single completion path. Guarded so the timeout, the response, and cancellation
            // can't double-emit or emit after complete. Declared as a hoisted function so the
            // timeout callback below can reference it before its textual position.
            let finished = false;
            function finish(state?: SuggestionsState): void {
                if (finished) {
                    return;
                }
                finished = true;
                clearTimeout(timeoutId);
                if (state) {
                    observer.next(state);
                }
                observer.complete();
            }

            // Error out (and cancel the request) if the backend never responds within the ceiling.
            const timeoutId = window.setTimeout(() => {
                finish({ state: 'error', questions: [] });
                abort.abort();
            }, this.requestTimeoutMs);

            // Through HttpClient, not fetch, so the request carries the org headers (and XSRF token) the
            // interceptors add. The AbortController stays the cancellation handle (shared with chat-start,
            // stop() and navigation when trackShared): aborting it cancels the HTTP request and completes
            // with no state. Listening before subscribing keeps that true even if the request settles
            // synchronously.
            const request = new Subscription();
            abort.signal.addEventListener('abort', () => {
                request.unsubscribe();
                finish();
            });
            request.add(
                this.http
                    .post<AskResponse>(ENDPOINT, { query, response_schema: QUESTIONS_SCHEMA } as AskRequest)
                    .subscribe({
                        next: data => {
                            const questions = parseQuestions(data?.response);
                            finish(
                                questions.length > 0
                                    ? { state: 'ready', questions }
                                    : { state: 'error', questions: [] },
                            );
                        },
                        // Non-2xx, network failure, or an unparseable body.
                        error: () => finish({ state: 'error', questions: [] }),
                    }),
            );

            return () => {
                clearTimeout(timeoutId);
                abort.abort();
                request.unsubscribe();
            };
        });
    }
}
